import {
  factory,
  Factory,
  getDefaultZIndex,
  ScrollAreaAutosizeProps,
  useProps,
} from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import { defaultSpotlightFilter } from './default-spotlight-filter';
import { isActionsGroup } from './is-actions-group';
import { limitActions } from './limit-actions';
import { spotlight, spotlightStore } from './spotlight.store';
import {
  SpotlightAction,
  SpotlightActionProps,
  type SpotlightActionStylesNames,
} from './SpotlightAction';
import {
  SpotlightActionsGroup,
  type SpotlightActionsGroupProps,
  type SpotlightActionsGroupStylesNames,
} from './SpotlightActionsGroup';
import {
  SpotlightActionsList,
  type SpotlightActionsListProps,
  type SpotlightActionsListStylesNames,
} from './SpotlightActionsList';
import {
  SpotlightEmpty,
  type SpotlightEmptyProps,
  type SpotlightEmptyStylesNames,
} from './SpotlightEmpty';
import {
  SpotlightFooter,
  type SpotlightFooterProps,
  type SpotlightFooterStylesNames,
} from './SpotlightFooter';
import { SpotlightRoot, SpotlightRootProps, SpotlightRootStylesNames } from './SpotlightRoot';
import {
  SpotlightSearch,
  SpotlightSearchProps,
  type SpotlightSearchStylesNames,
} from './SpotlightSearch';
import classes from './Spotlight.module.css';

export type SpotlightFilterFunction = (
  query: string,
  actions: SpotlightActions[]
) => SpotlightActions[];

export interface SpotlightActionData extends SpotlightActionProps {
  id: string;
  group?: string;
}

export interface SpotlightActionGroupData {
  group: string;
  actions: SpotlightActionData[];
}

export type SpotlightActions = SpotlightActionData | SpotlightActionGroupData;

export type SpotlightStylesNames = SpotlightRootStylesNames;

export interface SpotlightProps extends SpotlightRootProps {
  /** Props passed down to the `Spotlight.Search` */
  searchProps?: SpotlightSearchProps;

  /** Actions data, passed down to `Spotlight.Action` component */
  actions: SpotlightActions[];

  /** Function to filter actions data based on search query, by default actions are filtered by title, description and keywords */
  filter?: SpotlightFilterFunction;

  /** Message displayed when none of the actions match given `filter` */
  nothingFound?: React.ReactNode;

  /** Determines whether search query should be highlighted in action label @default false */
  highlightQuery?: boolean;

  /** Maximum number of actions displayed at a time @default Infinity */
  limit?: number;

  /** Props passed down to the `ScrollArea` component */
  scrollAreaProps?: Partial<ScrollAreaAutosizeProps>;
}

export type SpotlightFactory = Factory<{
  props: SpotlightProps;
  ref: HTMLDivElement;
  stylesNames: SpotlightStylesNames;
  staticComponents: {
    Search: typeof SpotlightSearch;
    ActionsList: typeof SpotlightActionsList;
    Action: typeof SpotlightAction;
    Empty: typeof SpotlightEmpty;
    Footer: typeof SpotlightFooter;
    ActionsGroup: typeof SpotlightActionsGroup;
    Root: typeof SpotlightRoot;
    open: typeof spotlight.open;
    close: typeof spotlight.close;
    toggle: typeof spotlight.toggle;
  };
}>;

const defaultProps = {
  size: 600,
  yOffset: 80,
  limit: Infinity,
  zIndex: getDefaultZIndex('max'),
  overlayProps: { backgroundOpacity: 0.35, blur: 7 },
  transitionProps: { duration: 200, transition: 'pop' },
  store: spotlightStore,
  filter: defaultSpotlightFilter,
  clearQueryOnClose: true,
  closeOnActionTrigger: true,
  shortcut: 'mod + K',
} satisfies Partial<SpotlightProps>;

export const Spotlight = factory<SpotlightFactory>((_props) => {
  const props = useProps('Spotlight', defaultProps, _props);
  const {
    searchProps,
    filter,
    query,
    onQueryChange,
    actions,
    nothingFound,
    highlightQuery,
    limit,
    scrollAreaProps,
    ...others
  } = props;

  const [_query, setQuery] = useUncontrolled({
    value: query,
    defaultValue: '',
    finalValue: '',
    onChange: onQueryChange,
  });

  const filteredActions = limitActions(filter(_query, actions), limit).map((item) => {
    if (isActionsGroup(item)) {
      const items = item.actions.map(({ id, ...actionData }) => (
        <SpotlightAction key={id} highlightQuery={highlightQuery} {...actionData} />
      ));

      return (
        <SpotlightActionsGroup key={item.group} label={item.group}>
          {items}
        </SpotlightActionsGroup>
      );
    }

    return <SpotlightAction key={item.id} highlightQuery={highlightQuery} {...item} />;
  });

  return (
    <SpotlightRoot {...others} query={_query} onQueryChange={setQuery}>
      <SpotlightSearch {...searchProps} />
      {filteredActions.length > 0 && (
        <SpotlightActionsList {...(scrollAreaProps as any)}>{filteredActions}</SpotlightActionsList>
      )}
      {filteredActions.length === 0 && nothingFound && (
        <SpotlightEmpty>{nothingFound}</SpotlightEmpty>
      )}
    </SpotlightRoot>
  );
});

Spotlight.classes = classes;
Spotlight.displayName = '@mantine/spotlight/Spotlight';
Spotlight.Search = SpotlightSearch;
Spotlight.ActionsList = SpotlightActionsList;
Spotlight.Action = SpotlightAction;
Spotlight.Empty = SpotlightEmpty;
Spotlight.ActionsGroup = SpotlightActionsGroup;
Spotlight.Footer = SpotlightFooter;
Spotlight.Root = SpotlightRoot;
Spotlight.open = spotlight.open;
Spotlight.close = spotlight.close;
Spotlight.toggle = spotlight.toggle;

export namespace Spotlight {
  export type Props = SpotlightProps;
  export type StylesNames = SpotlightStylesNames;
  export type Factory = SpotlightFactory;
  export type FilterFunction = SpotlightFilterFunction;
  export type ActionData = SpotlightActionData;
  export type ActionGroupData = SpotlightActionGroupData;

  export namespace Action {
    export type Props = SpotlightActionProps;
    export type StylesNames = SpotlightActionStylesNames;
  }

  export namespace ActionsGroup {
    export type Props = SpotlightActionsGroupProps;
    export type StylesNames = SpotlightActionsGroupStylesNames;
  }

  export namespace ActionsList {
    export type Props = SpotlightActionsListProps;
    export type StylesNames = SpotlightActionsListStylesNames;
  }

  export namespace Empty {
    export type Props = SpotlightEmptyProps;
    export type StylesNames = SpotlightEmptyStylesNames;
  }

  export namespace Footer {
    export type Props = SpotlightFooterProps;
    export type StylesNames = SpotlightFooterStylesNames;
  }

  export namespace Search {
    export type Props = SpotlightSearchProps;
    export type StylesNames = SpotlightSearchStylesNames;
  }

  export namespace Root {
    export type Props = SpotlightRootProps;
    export type StylesNames = SpotlightRootStylesNames;
  }
}
